import { appGrants, appPermissions } from "../modules/app";
import { analysisGrants, analysisPermissions } from "../modules/analysis";
import { billingGrants, billingPermissions } from "../modules/billing";
import { campaignGrants, campaignPermissions } from "../modules/campaignRecord";
import { copilotGrants, copilotPermissions } from "../modules/copilot";
import { crmGrants, crmPermissions } from "../modules/crm";
import {
  deliveryDocumentationGrants,
  deliveryDocumentationPermissions,
} from "../modules/delivery-documentation";
import { faultGrants, faultPermissions } from "../modules/faults";
import { guidelineGrants, guidelinePermissions } from "../modules/guidelines";
import { logGrants, logPermissions } from "../modules/logs";
import { mailGrants, mailPermissions } from "../modules/mail";
import { ludConnectGrants, ludConnectPermissions } from "../modules/lud-connect";
import { ludDashboardGrants, ludDashboardPermissions } from "../modules/lud-dashboard";
import { ludDocumentGrants, ludDocumentPermissions } from "../modules/lud-documents";
import { ludGrants, ludPermissions } from "../modules/lud";
import { menuGrants, menuPermissions } from "../modules/menu";
import { notificationGrants, notificationPermissions } from "../modules/notifications";
import { onboardingGrants, onboardingPermissions } from "../modules/onboarding";
import { operationGrants, operationPermissions } from "../modules/operation";
import { reportGrants, reportPermissions } from "../modules/reports";
import { restaurantGrants, restaurantPermissions } from "../modules/restaurants";
import { saledItemGrants, saledItemPermissions } from "../modules/saled-items";
import {
  storeOnboardingAdminGrants,
  storeOnboardingAdminPermissions,
} from "../modules/store-onboarding-admin";
import { supportGrants, supportPermissions } from "../modules/support";
import { userGrants, userPermissions } from "../modules/users";

// Every module contributes (a) a nested map of permission ids, for autocomplete
// and discoverability, and (b) a flat id -> rule map holding who may do it.
export const PERMISSIONS = Object.freeze({
  app: appPermissions,
  analysis: analysisPermissions,
  billing: billingPermissions,
  campaign: campaignPermissions,
  copilot: copilotPermissions,
  crm: crmPermissions,
  deliveryDocumentation: deliveryDocumentationPermissions,
  fault: faultPermissions,
  guideline: guidelinePermissions,
  log: logPermissions,
  mail: mailPermissions,
  ludConnect: ludConnectPermissions,
  ludDashboard: ludDashboardPermissions,
  ludDocument: ludDocumentPermissions,
  lud: ludPermissions,
  menu: menuPermissions,
  notification: notificationPermissions,
  onboarding: onboardingPermissions,
  operation: operationPermissions,
  report: reportPermissions,
  restaurant: restaurantPermissions,
  saledItem: saledItemPermissions,
  storeOnboardingAdmin: storeOnboardingAdminPermissions,
  support: supportPermissions,
  user: userPermissions,
});

const MODULE_GRANTS = [
  appGrants,
  analysisGrants,
  billingGrants,
  campaignGrants,
  copilotGrants,
  crmGrants,
  deliveryDocumentationGrants,
  faultGrants,
  guidelineGrants,
  logGrants,
  mailGrants,
  ludConnectGrants,
  ludDashboardGrants,
  ludDocumentGrants,
  ludGrants,
  menuGrants,
  notificationGrants,
  onboardingGrants,
  operationGrants,
  reportGrants,
  restaurantGrants,
  saledItemGrants,
  storeOnboardingAdminGrants,
  supportGrants,
  userGrants,
];

const flattenIds = (node, out = []) => {
  if (typeof node === "string") out.push(node);
  else if (node && typeof node === "object") {
    Object.values(node).forEach((child) => flattenIds(child, out));
  }
  return out;
};

export const ALL_PERMISSION_IDS = Object.freeze(flattenIds(PERMISSIONS));
export const PERMISSION_SET = new Set(ALL_PERMISSION_IDS);

export const GRANTS = Object.freeze(Object.assign({}, ...MODULE_GRANTS));

// Fail fast (at import time, in every environment) when a module is malformed:
// duplicate ids, grants for unknown ids, or ids nobody can ever hold.
(() => {
  if (PERMISSION_SET.size !== ALL_PERMISSION_IDS.length) {
    const seen = new Set();
    const dup = ALL_PERMISSION_IDS.find((id) => seen.size === seen.add(id).size);
    throw new Error(`[access] duplicate permission id: ${dup}`);
  }
  MODULE_GRANTS.forEach((grants) => {
    Object.keys(grants).forEach((id) => {
      if (!PERMISSION_SET.has(id)) {
        throw new Error(`[access] grant for unregistered permission: ${id}`);
      }
    });
  });
  const totalGrants = MODULE_GRANTS.reduce((n, g) => n + Object.keys(g).length, 0);
  if (Object.keys(GRANTS).length !== totalGrants) {
    throw new Error("[access] the same permission is granted in two modules");
  }
  ALL_PERMISSION_IDS.forEach((id) => {
    if (!GRANTS[id]) throw new Error(`[access] permission without a grant: ${id}`);
  });
})();
